import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, InjectionToken, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AttachmentData, SimSlideshowComponent } from '@simOn/common/carousel';
import { FileDownloadService } from '@simOn/common/media';
import { SimButtonModule } from '@simOn/ui/sim-button';
import { SimIconComponent, SimIconType } from '@simOn/ui/sim-icon';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { defer, EMPTY, map, Observable, shareReplay, take } from 'rxjs';
import {
  ACCEPTED_AUDIO_EXTENSIONS,
  ACCEPTED_IMAGE_EXTENSIONS,
  ACCEPTED_PDF_EXTENSIONS,
  ACCEPTED_VIDEO_EXTENSIONS
} from '../models/accepted-extensions.const';

export type AttachmentPreviewModalData = {
  name: string;
  fileId: string;
  source?: () => Observable<string>;
  hasDownloadAction?: boolean;
};

export type ResourceDownloadUrl = {
  getResourceSource$: (fileId: string | number) => Observable<string>;
};

/**
 * Optional app-wide resolver of a file id to its download URL.
 * When absent, each preview must pass its own `source` callback in the dialog data.
 */
export const ResourcesDownloadUrlToken = new InjectionToken<ResourceDownloadUrl>('Resources download url token');

@Component({
  imports: [AsyncPipe, SimSlideshowComponent, SimButtonModule, SimIconComponent, SimSpinnerComponent],
  templateUrl: './attachment-preview-modal.component.html',
  styleUrl: './attachment-preview-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'attachment-preview-modal'
  }
})
export class AttachmentPreviewModalComponent {
  private readonly _dialogRef = inject(DialogRef);
  private readonly _fileDownload = inject(FileDownloadService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _resourceSourceToken = inject(ResourcesDownloadUrlToken, { optional: true });

  protected readonly data = inject<AttachmentPreviewModalData>(DIALOG_DATA);

  private readonly _resourceSource$: Observable<string> = defer(() => {
    if (this.data.source === undefined && this._resourceSourceToken === null) {
      console.error(`Resource ${this.data.name} has no source provided`);
      return EMPTY;
    }
    return this.data.source?.() ?? this._resourceSourceToken?.getResourceSource$(this.data.fileId) ?? EMPTY;
  }).pipe(shareReplay({ bufferSize: 1, refCount: false }));

  protected readonly extension = this.data.name?.split('.').pop()?.toLowerCase() ?? '';
  protected readonly isPhotoDocument = (ACCEPTED_IMAGE_EXTENSIONS as readonly string[]).includes(this.extension);
  protected readonly isVideoDocument = (ACCEPTED_VIDEO_EXTENSIONS as readonly string[]).includes(this.extension);
  protected readonly isAudioDocument = (ACCEPTED_AUDIO_EXTENSIONS as readonly string[]).includes(this.extension);
  protected readonly isPdfDocument = (ACCEPTED_PDF_EXTENSIONS as readonly string[]).includes(this.extension);

  protected readonly fallbackIcon: SimIconType = this.isAudioDocument ? 'play' : 'insert_drive_file';

  /** Single-element collection for the shared slideshow — `urlWithToken` skips its internal URL regeneration. */
  protected readonly slideshowCollection$: Observable<AttachmentData[]> = this._resourceSource$.pipe(
    map((url) => [
      {
        key: this.data.fileId,
        dataType: 'image',
        name: this.data.name,
        url,
        urlWithToken: url
      }
    ])
  );

  protected readonly mediaSource$: Observable<string> = this._resourceSource$;

  protected readonly pdfSource$: Observable<SafeResourceUrl> = this._resourceSource$.pipe(
    map((url) => this._sanitizer.bypassSecurityTrustResourceUrl(url))
  );

  protected close(): void {
    this._dialogRef.close();
  }

  protected download(): void {
    this._resourceSource$.pipe(take(1)).subscribe((source) => {
      this._fileDownload.download(source, this.data.name);
    });
  }
}
